import React, { useState } from 'react';
import { CustomRegistrationQuestion, RegistrationQuestion } from '../types/RegistrationQuestion';
import { CreateEvent } from '../types/Event';
import { v4 as uuidv4 } from 'uuid';
import { MEDIA_FILE_IDS } from '../constants/media-files';
import { getIcon } from '../services/iconService';
import { EditCustomQuestions } from './EditCustomQuestions';
import ToggleSwitch from './ToggleSwitch';
import { useToast } from '../context/ToastContext';

interface RegistrationQuestionsPageProps {
  eventDetails: CreateEvent;
  registrationQuestions: RegistrationQuestion[];
  eventType: string;
  handleQuestionOptionChange: (questionId: string, option: 'Required' | 'Optional' | 'Omit') => void;
  customRegistrationQuestions: CustomRegistrationQuestion[];
  setCustomRegistrationQuestions: React.Dispatch<React.SetStateAction<CustomRegistrationQuestion[]>>;
}

export const RegistrationQuestionsPage: React.FC<RegistrationQuestionsPageProps> = ({
  eventDetails,
  registrationQuestions,
  eventType,
  handleQuestionOptionChange,
  customRegistrationQuestions,
  setCustomRegistrationQuestions
}) => {
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [newQuestionText, setNewQuestionText] = useState("");
  const [newQuestionOptions, setNewQuestionOptions] = useState<string[]>([]);
  const [newQuestionType, setNewQuestionType] = useState<'text' | 'select'>("text");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingType, setEditingType] = useState<'text' | 'select'>("text");
  const [editingRequired, setEditingRequired] = useState(false);
  const [newQuestionRequired, setNewQuestionRequired] = useState(false);
  const [editingOptions, setEditingOptions] = useState<string[]>([]);

  const { addToast } = useToast();


  // State for expanded categories
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const handleToggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSaveEditing = () => {
    if (editingIndex === null) return;

    setCustomRegistrationQuestions(prev =>
      prev.map((q, index) =>
        index === editingIndex
          ? {
            ...q,
            text: editingText,
            type: editingType,
            required: editingRequired,
            options: editingType === 'select' ? editingOptions : []
          }
          : q
      )
    );

    // optional: exit edit mode
    setEditingIndex(null);
  };

  const toggleNewQuestionRequired = () => {
    setNewQuestionRequired(!newQuestionRequired);
  }

  const handleAdd = () => {
    if (customRegistrationQuestions.length < 100) {
      if (newQuestionType.toLowerCase() == 'select') {
        const hasEmptyOption = newQuestionOptions.some(
          option => option === ''
        );
        if (hasEmptyOption) {
          addToast('Question has an empty option.', true);
          return;
        }
      }
      const customQuestion: CustomRegistrationQuestion = {
        event_id: '',
        custom_registration_question_id: '',
        custom_question_text: newQuestionText,
        is_required: newQuestionRequired,
        question_type: newQuestionType,
        options: newQuestionType.toLowerCase() == 'select' ? newQuestionOptions : []
      };
      setCustomRegistrationQuestions(prev => [...prev, customQuestion]);
      setNewQuestionOptions([]);
      setNewQuestionRequired(false);
      setNewQuestionText('');
      setNewQuestionType('text');
    } else {
      addToast('Custom question limit reached');
      console.log("Too many questions");
    }
  }

  const handleDelete = (indexToRemove: number) => {
    setCustomRegistrationQuestions(prev =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  }

  const filteredQuestions = registrationQuestions.filter((question) => {
    const isMedia = question.preset_registration_question_id in MEDIA_FILE_IDS;
    if (isMedia && !eventDetails.has_media) {
      return false;
    }
    if (eventType === 'MUSIC') {
      return question.is_music;
    } else if (eventType === 'SPORTS') {
      return question.is_sports;
    } else {
      return question.is_general;
    }
  });

  const groupedByCategory: Record<string, RegistrationQuestion[]> = filteredQuestions.reduce((groups, question) => {
    const category = question.category || 'Uncategorized';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(question);
    return groups;
  }, {} as Record<string, RegistrationQuestion[]>);

  for (const category in groupedByCategory) {
    groupedByCategory[category].sort((a, b) => a.display_order - b.display_order);
  }

  const handleInfoClick = (questionId: string) => {
    setSelectedQuestionId(prevId => (prevId === questionId ? null : questionId));
  };

  const handleAddCustomQuestion = () => {
    if (customRegistrationQuestions.length >= 10) return;
    if (!newQuestionText.trim()) return;
    setCustomRegistrationQuestions([
      ...customRegistrationQuestions,
      {
        event_id: '',
        custom_registration_question_id: uuidv4(),
        custom_question_text: newQuestionText.trim(),
        is_required: false,
        options: [],
        question_type: 'text'
      }
    ]);
    setNewQuestionText('');
  };

  const handleCustomRequiredChange = (id: string, isRequired: boolean) => {
    setCustomRegistrationQuestions(prevQuestions =>
      prevQuestions.map(q =>
        q.custom_registration_question_id === id
          ? { ...q, is_required: isRequired }
          : q
      )
    );
  };

  const handleCustomTypeChange = (
    id: string,
    type: 'text' | 'select'
  ) => {
    setCustomRegistrationQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.custom_registration_question_id !== id) return q;

        return {
          ...q,
          question_type: type,
          options:
            type === 'select'
              ? q.options && q.options.length > 0
                ? q.options
                : ['']
              : [],
        };
      })
    );
  };


  const updateQuestionOptions = (
    questionId: string,
    updater: (options: string[]) => string[]
  ) => {
    setCustomRegistrationQuestions(prev =>
      prev.map(q =>
        q.custom_registration_question_id === questionId
          ? { ...q, options: updater(q.options ?? ['']) }
          : q
      )
    );
  };


  return (
    <div className='card' style={{ maxWidth: '750px' }}>
      <h1>Registration Questions</h1>
      <p>Add questions to your event registration form to collect important information from registrants. This data can help with event planning, communication, and participant management.</p>
      <h2>* Name, Email, and Phone Number will be collected by default.</h2>

      {/* Custom Questions Section */}
      <div style={{ marginTop: '20px' }}>
        <h2>Custom Questions</h2>

        <div>

          {customRegistrationQuestions.map((q, index) => (
            <div key={q.custom_registration_question_id} style={{ marginBottom: 12, gap: '5px' }}>
              {editingIndex === index ? (
                <div className="card">
                  <div className='buttonsRow'>
                    <input
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      style={{ maxWidth: '270px' }}
                    />

                  </div>
                  {editingType == 'select' && <div style={{ marginLeft: '20px' }}>
                    {editingOptions.map((option, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                        <input
                          value={option}
                          onChange={(e) => {
                            const updatedOptions = [...editingOptions];
                            updatedOptions[i] = e.target.value;
                            setEditingOptions(updatedOptions);
                          }}
                          placeholder={`Option ${i + 1}`}
                          style={{ maxWidth: '300px' }}
                          min={1}
                          max={500}
                        />
                        <button
                          type="button"
                          className="cu-icon-button"
                          style={{ marginBottom: '10px' }}
                          disabled={editingOptions.length <= 1}
                          onClick={() => {
                            const updatedOptions = editingOptions.filter((_, idx) => idx !== i);
                            if (updatedOptions.length == 0) {
                              setEditingOptions([""]);
                            } else {
                              setEditingOptions(updatedOptions);
                            }

                          }}
                        >
                          <img src={getIcon('delete')} alt={'delete'}></img>
                        </button>
                      </div>
                    ))}

                    <button
                      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0px', backgroundColor: 'white', outline: 'none', border: 'none', boxShadow: 'none' }}
                      type="button"
                      disabled={editingOptions.length > 25}
                      onClick={() => setEditingOptions([...editingOptions, ''])}>
                      <img style={{ height: '40px', width: '45px', aspectRatio: 'fit' }} src={getIcon('add')} alt={'Add'}></img>

                      <span style={{ color: '#333', marginRight: '12px', backgroundColor: 'none', boxShadow: 'none' }}>New Option</span>
                    </button>
                  </div>}
                  <div className="buttonsRow">
                    <div className="buttonsLeft" />
                    <div className='buttonsRight' style={{ gap: '5px', flexWrap: 'nowrap' }}>
                      <select
                        value={editingType}
                        onChange={(e) => setEditingType(e.target.value as "text" | "select")}
                        style={{ maxWidth: '100px', marginBottom: '0px' }}
                      >
                        <option value="text">Text</option>
                        <option value="select">Select</option>
                      </select>
                      <ToggleSwitch label={editingRequired ? 'Required' : 'Optional'} checked={editingRequired} onChange={() => { setEditingRequired(!editingRequired); }} />
                      <button onClick={() => { handleSaveEditing(); }}>Set</button>
                      <button className='cancelButton' onClick={() => setEditingIndex(null)}>Cancel</button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className='buttonsRow'>
                    <div className='buttonsLeft' style={{ flexDirection: 'column' }}>
                      <span><strong>{q.custom_question_text}</strong></span>
                      {q.question_type === 'select' && q.options && q.options?.length > 0 && (
                        <div style={{ marginLeft: '5px' }}>
                          <div>Options:</div>
                          <ul style={{ marginTop: '4px', marginBottom: 0 }}>
                            {q.options.map((opt, idx) => (
                              <li key={idx}>{opt}</li>
                            ))}
                          </ul>
                        </div>
                      )}                                </div>
                  </div>
                  <div className="buttonsRow">
                    <div className="buttonsLeft" />
                    <div className='buttonsRight' style={{ flexWrap: 'nowrap', gap: '5px' }}>
                      <select
                        disabled={true}
                        value={q.question_type}
                        style={{ maxWidth: '100px', marginBottom: '0px' }}
                      >
                        <option value="text">Text</option>
                        <option value="select">Select</option>
                      </select>
                      <ToggleSwitch label={q.is_required ? 'Required' : 'Optional'} disabled={true} checked={q.is_required} onChange={() => { }} />
                      <button className="cu-icon-button" onClick={() => {
                        setEditingIndex(index);
                        setEditingText(q.custom_question_text);
                        setEditingRequired(q.is_required);
                        setEditingType(q.question_type as 'text' | 'select');
                        setEditingOptions(q.question_type == 'select' ? q.options ?? [''] : ['']);
                      }}
                      ><img src={getIcon('edit')} alt={'Edit'}></img></button>
                      <button className='cu-icon-button' onClick={() => handleDelete(index)}>
                        <img src={getIcon('delete')} alt={'Delete'}></img>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
          }

          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: '5px' }}>
              <input
                value={newQuestionText}
                onChange={(e) => setNewQuestionText(e.target.value)}
                placeholder="New custom question"
                style={{ maxWidth: '400px' }}
                min={1}
                max={500}
              />
              <select
                value={newQuestionType}
                onChange={(e) => setNewQuestionType(e.target.value as "text" | "select")}
                style={{ maxWidth: '100px' }}
              >
                <option value="text">Text</option>
                <option value="select">Select</option>
              </select>
              <ToggleSwitch label={newQuestionRequired ? 'Required' : 'Optional'} checked={newQuestionRequired} onChange={toggleNewQuestionRequired} />
            </div>
            {/* Options inputs */}
            {newQuestionType === 'select' && (
              <div style={{ marginBottom: '10px', marginLeft: '20px' }}>
                {newQuestionOptions.map((option, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                    <input
                      value={option}
                      onChange={(e) => {
                        const updatedOptions = [...newQuestionOptions];
                        updatedOptions[i] = e.target.value;
                        setNewQuestionOptions(updatedOptions);
                      }}
                      placeholder={`Option ${i + 1}`}
                      style={{ maxWidth: '300px' }}
                      min={1}
                      max={500}
                    />
                    <button
                      type="button"
                      className="cu-icon-button"
                      style={{ marginBottom: '10px' }}
                      disabled={newQuestionOptions.length <= 1}
                      onClick={() => {
                        const updatedOptions = newQuestionOptions.filter((_, idx) => idx !== i);
                        if (updatedOptions.length == 0) {
                          setNewQuestionOptions([""]);
                        } else {
                          setNewQuestionOptions(updatedOptions);
                        }

                      }}
                    >
                      <img src={getIcon('delete')} alt={'delete'}></img>
                    </button>
                  </div>
                ))}

                <button
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0px', backgroundColor: 'white', outline: 'none', border: 'none', boxShadow: 'none' }}
                  type="button"
                  disabled={newQuestionOptions.length > 25}
                  onClick={() => setNewQuestionOptions([...newQuestionOptions, ''])}>
                  <img style={{ height: '40px', width: '45px', aspectRatio: 'fit' }} src={getIcon('add')} alt={'Add'}></img>

                  <span style={{ color: '#555' }}>New Option</span>
                </button>
                <br />
              </div>
            )}
            <div className="buttonsRow">
              <div className="buttonsLeft" />
              <div className="buttonsRight">
                <button
                  onClick={handleAdd}
                  disabled={newQuestionText == '' || (newQuestionType == 'select' && newQuestionOptions.length == 0) || customRegistrationQuestions.length >= 100}
                >
                  Add Custom Question
                </button>
              </div>
            </div>
          </div>
        </div >
        {/* <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <input
            type="text"
            value={newQuestionText}
            onChange={(e) => setNewQuestionText(e.target.value)}
            placeholder="Enter custom question"
            maxLength={500}
            style={{ flex: 1 }}
          />
          <button
          className='cu-icon-button'
            onClick={handleAddCustomQuestion}
            disabled={customRegistrationQuestions.length >= 100 || newQuestionText.length == 0}
          >
            <img src={getIcon('add')} alt={'add'}></img>
          </button>
        </div> */}

        {/* {customRegistrationQuestions.map((q) => {
          const boxStyles: React.CSSProperties = {
            backgroundColor: 'white',
            borderColor: q.is_required ? 'blue' : 'transparent',
            borderWidth: q.is_required ? '2px' : '1px',
            borderStyle: 'solid',
            borderRadius: '5px',
            padding: '10px',
            maxWidth: '400px',
            marginBottom: '10px',
            transition: 'all 0.3s ease',
          };

          return (
            <div key={q.custom_registration_question_id} style={boxStyles} className='card'>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h4 style={{ flex: 1 }}><strong>{q.custom_question_text}</strong></h4>
                <button
                  onClick={() => handleRemoveCustomQuestion(q.custom_registration_question_id)}
                  style={{ marginLeft: '10px' }}
                  className='cu-icon-button'
                >
                 <img src={getIcon('delete')} alt={'delete'}></img>
                </button>
              </div>

              <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'right' }}>
                <select
                  value={q.question_type}
                  onChange={(e) => handleCustomTypeChange(q.custom_registration_question_id, e.target.value as "text" | "select")}
                  style={{ maxWidth: '100px', marginBottom: '0px' }}
                >
                  <option value="text">Text</option>
                  <option value="select">Select</option>
                </select>
                <label style={{ marginLeft: '10px' }}>
                  <input
                    type="radio"
                    name={`custom-${q.custom_registration_question_id}`}
                    value="true"
                    checked={q.is_required === true}
                    onChange={() => handleCustomRequiredChange(q.custom_registration_question_id, true)}
                  />
                  Required
                </label>
                <label style={{ marginLeft: '10px' }}>
                  <input
                    type="radio"
                    name={`custom-${q.custom_registration_question_id}`}
                    value="false"
                    checked={q.is_required === false}
                    onChange={() => handleCustomRequiredChange(q.custom_registration_question_id, false)}
                  />
                  Optional
                </label>
              </div>
              {q.question_type === 'select' && (
                <div>
                  <h4>Options</h4>

                  {(q.options ?? ['']).map((option, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        marginBottom: '5px',
                        marginLeft: '10px'
                      }}
                    >
                      <input
                        value={option}
                        onChange={(e) =>
                          updateQuestionOptions(q.custom_registration_question_id, opts => {
                            const next = [...opts];
                            next[i] = e.target.value;
                            return next;
                          })
                        }
                        placeholder={`Option ${i + 1}`}
                        maxLength={500}
                        style={{ maxWidth: '300px' }}
                      />

                      <button
                        type="button"
                        className="cu-icon-button"
                        style={{ marginBottom: '10px' }}
                        disabled={(q.options?.length ?? 1) <= 1}
                        onClick={() =>
                          updateQuestionOptions(q.custom_registration_question_id, opts =>
                            opts.length > 1 ? opts.filter((_, idx) => idx !== i) : ['']
                          )
                        }
                      >
                      <img src={getIcon('delete')} alt={'delete'}></img>

                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    style={{ marginLeft: '10px' }}
                    disabled={(q.options?.length ?? 0) >= 25}
                    onClick={() =>
                      updateQuestionOptions(q.custom_registration_question_id, opts => [
                        ...opts,
                        '',
                      ])
                    }
                  >
                    Add New Option
                  </button>
                </div>
              )}

            </div>
          );
        })} */}
      </div>

      <h2>Preset Questions</h2>

      {Object.keys(groupedByCategory).map((category) => {
        const isExpanded = expandedCategories[category] ?? false; // default collapsed
        return (
          <div key={category} style={{ marginBottom: '20px' }}>
            {/* Category Header with Toggle Button */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => handleToggleCategory(category)}>
              <h3>{category} Questions:</h3>
              <button aria-label='Expand and Collapse Category Button'
                style={{ color: 'black', background: 'none', outline: 'none', boxShadow: 'none', padding: '0px' }}>
                {!isExpanded && <span>&#9654;</span>}
                {!!isExpanded && <span>&#9660;</span>}
              </button>
            </div>

            {/* Only render questions if expanded */}
            {isExpanded && (
              <div style={{ marginTop: '10px' }}>
                {groupedByCategory[category].map((question) => {
                  const selectedOption = question.option || 'Optional';

                  let boxStyles: React.CSSProperties = {
                    backgroundColor: 'white',
                    borderColor: 'transparent',
                    borderWidth: '1px',
                    borderRadius: '5px',
                    padding: '10px',
                    maxWidth: '400px',
                    marginBottom: '2px',
                    transition: 'all 0.3s ease',
                  };

                  if (selectedOption === 'Required') {
                    boxStyles = { ...boxStyles, borderColor: 'blue', borderWidth: '2px' };
                  } else if (selectedOption === 'Omit') {
                    boxStyles = { ...boxStyles, backgroundColor: '#d2d2d2' };
                  }

                  return (
                    <div key={question.preset_registration_question_id} style={boxStyles} className='card'>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <h4 style={{ flex: 1 }}>{question.question_text}</h4>
                        <button onClick={() => handleInfoClick(question.preset_registration_question_id)}>Info</button>
                      </div>
                      {selectedQuestionId === question.preset_registration_question_id && (
                        <div style={{
                          backgroundColor: '#f0f0f0',
                          padding: '10px',
                          marginTop: '10px',
                          borderRadius: '5px',
                          border: '1px solid #ccc'
                        }}>
                          <strong>Description:</strong> {question.description || 'No description available.'}
                        </div>
                      )}
                      <div style={{ marginTop: '0px', display: 'flex', justifyContent: 'right' }}>
                        {['Required', 'Optional', 'Omit'].map((option) => (
                          <label key={option} style={{ marginLeft: '10px' }}>
                            <input
                              type="radio"
                              name={`question-${question.preset_registration_question_id}`}
                              value={option}
                              checked={selectedOption === option}
                              onChange={() => handleQuestionOptionChange(question.preset_registration_question_id, option as any)}
                            />
                            {option}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}


    </div>
  );
};
